/**
 * 前台 TOC 数据接口层
 * 使用 gen-api 生成的 CService（/api/client/cms）与 CStoreService（/api/store）对接后端
 */

import type { BaseResponseDto_AnyResponseDto } from '@/apis/models/BaseResponseDto_AnyResponseDto';
import { CService } from '@/apis/services/CService';
import { CStoreService } from '@/apis/services/CStoreService';

function unwrapData<T>(res: BaseResponseDto_AnyResponseDto): T {
  if (res.errNo !== 0) {
    throw new Error((res as { msg?: string }).msg ?? 'Request failed');
  }
  const raw = res.data as unknown as Record<string, unknown>;
  // 兼容后端多包一层 data 的情况：{ data: { hero, ... } } -> 取内层
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in raw &&
    raw.data &&
    typeof raw.data === 'object'
  ) {
    return raw.data as unknown as T;
  }
  return raw as unknown as T;
}

export interface SiteConfigNavItem {
  key: string;
  label: string;
  icon: string;
}

export interface SiteConfigFooter {
  brandName: string;
  description: string;
  quickLinks: { label: string; path: string }[];
  contact: {
    email: string;
    phone: string;
    address?: string;
    whatsapp?: string;
  };
  copyright: string;
}

export interface SiteConfigTheme {
  heroBackgroundImage?: string;
  heroGradientStart?: string;
  heroGradientEnd?: string;
}

export interface SiteConfig {
  brandName: string;
  logoText: string;
  logoImage: string;
  navItems: SiteConfigNavItem[];
  footer: SiteConfigFooter;
  theme?: SiteConfigTheme;
  /** WhatsApp contact number (e.g. 1234567890 or +1234567890), used for mobile float button */
  whatsappContact?: string;
}

export interface ProductConfig {
  description: string;
}

/** 独立导出，供首屏/分块场景稳定使用，避免 storefrontApi 对象在部分 chunk 中被 tree-shake 导致方法缺失 */
export async function getProductConfig(): Promise<ProductConfig> {
  const res = await CService.cmsControllerProductConfig();
  return unwrapData<ProductConfig>(res);
}

/** 首页/横幅轮播项，接口 GET /api/store/banners?position=home；支持按分辨率提供不同图片 */
export interface BannerItem {
  id: string;
  title?: string;
  /** 默认图，必填；无 imageMobile/imageDesktop 时全分辨率使用 */
  image: string;
  /** 移动端图片（如 <768px），可选 */
  imageMobile?: string;
  /** PC 端图片（如 ≥768px），可选 */
  imageDesktop?: string;
  link?: string;
  sortOrder?: number;
}

export interface HomeData {
  hero: {
    title: string;
    description: string;
    primaryButton: { text: string; path: string };
    secondaryButton: { text: string; path: string };
  };
  categoriesSection: { title: string; description: string; moreText?: string };
  aboutIntro?: {
    mediaType?: 'image' | 'video';
    mediaUrl?: string;
    title?: string;
    paragraph1?: string;
    paragraph2?: string;
    buttonText?: string;
    buttonPath?: string;
  };
  services?: {
    items?: { icon: string; title: string; description: string }[];
  };
  featuredProducts?: {
    eyebrow?: string;
    title?: string;
    buttonText?: string;
    items?: { image: string; title: string; link: string }[];
  };
  projectStories?: {
    title?: string;
    description?: string;
    items?: { image: string; label: string; link?: string }[];
  };
  cta: {
    title: string;
    description: string;
    buttonText: string;
    buttonPath: string;
  };
}

export interface CategoryItem {
  key: string;
  title: string;
  description: string;
  icon: string;
  path: string;
  image?: string;
  subtitle?: string;
  highlights?: string[];
}

export interface ProductListItem {
  id: string;
  name: string;
  category: string;
  summary: string;
  /** 列表缩略图（主图第一张或单张） */
  image: string | null;
  price: string;
}

/** 产品详情：主图多张轮播、介绍图、富文本内容 */
export interface ProductDetail {
  id: string;
  name: string;
  category: string;
  summary: string;
  /** 主图，多张用于轮播；兼容单张 string */
  image: string | string[];
  price: string;
  description: string;
  /** 介绍图，多张，在内容区优先展示 */
  images?: string[];
  /** 富文本内容（HTML），在介绍图下方展示 */
  content?: string;
  specs: { label: string; value: string }[];
}

export interface AboutBlock {
  icon: string;
  title: string;
  text: string;
}

export interface AboutStoryBlock {
  image: string;
  title: string;
  text: string;
  imagePosition?: 'left' | 'right';
}

export interface AboutData {
  heroImage?: string;
  title: string;
  lead: string;
  blocks: AboutBlock[];
  storyBlocks?: AboutStoryBlock[];
  factoryDisplay?: {
    title?: string;
    images?: string[];
  };
  content?: string;
}

export interface ContactPageData {
  title: string;
  description: string;
  sidebarTitle: string;
  contact: {
    email: string;
    phone: string;
    address?: string;
    whatsapp?: string;
  };
  sidebarNote: string;
}

export interface NewsListItem {
  id: string;
  title: string;
  summary: string;
  publishedAt: string;
  coverImage?: string | null;
}

export interface NewsDetailData extends NewsListItem {
  body: string;
}

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

/** 与后端一致：client=顾客，manager=官方/客服；readByManagerAt 表示该条客户消息已被客服读过 */
export interface ChatMessage {
  id: string;
  content: string;
  from: 'client' | 'manager';
  createdAt: string;
  /** 客服已读时间戳（毫秒），仅客户消息可能有 */
  readByManagerAt?: number;
}

export const storefrontApi = {
  getSiteConfig: async (): Promise<SiteConfig> => {
    const res = await CService.cmsControllerSiteConfig();
    return unwrapData<SiteConfig>(res);
  },

  getHome: async (): Promise<HomeData> => {
    const res = await CService.cmsControllerHome();
    return unwrapData<HomeData>(res);
  },

  /** 轮播/横幅列表，position 如 'home'（后端未区分时忽略） */
  getBanners: async (position?: string): Promise<BannerItem[]> => {
    void position; // 预留，后端按 position 筛选时使用
    const res = await CService.cmsControllerBanners();
    const d = unwrapData<{ list?: BannerItem[] }>(res);
    const list = d?.list ?? [];
    return list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  },

  getCategories: async (): Promise<CategoryItem[]> => {
    const res = await CService.cmsControllerCategories();
    const d = unwrapData<{ list?: CategoryItem[] }>(res);
    return d?.list ?? [];
  },

  getProducts: async (params?: {
    page?: number;
    pageSize?: number;
    category?: string;
    keyword?: string;
  }): Promise<{ list: ProductListItem[]; total: number }> => {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 8;
    const category =
      params?.category && params.category !== 'all' ? params.category : '';
    const res = await CService.cmsControllerProducts(category, page, pageSize);
    const d = unwrapData<{ list?: ProductListItem[]; total?: number }>(res);
    let list = d?.list ?? [];
    const total = d?.total ?? list.length;
    if (params?.keyword) {
      const k = params.keyword.toLowerCase();
      list = list.filter(
        p =>
          p.name.toLowerCase().includes(k) ||
          (p.summary && p.summary.toLowerCase().includes(k))
      );
    }
    return { list, total };
  },

  getProductDetail: async (id: string): Promise<ProductDetail> => {
    const numId = parseInt(id, 10);
    if (Number.isNaN(numId)) throw new Error('Invalid product id');
    const res = await CService.cmsControllerProduct(numId);
    return unwrapData<ProductDetail>(res);
  },

  getAbout: async (): Promise<AboutData> => {
    const res = await CService.cmsControllerAbout();
    return unwrapData<AboutData>(res);
  },

  getProductConfig,

  getContactPage: async (): Promise<ContactPageData> => {
    const res = await CService.cmsControllerContact();
    return unwrapData<ContactPageData>(res);
  },

  submitContact: async (data: {
    name: string;
    email: string;
    phone?: string;
    whatsapp?: string;
    company?: string;
    message: string;
  }): Promise<void> => {
    const res = await CStoreService.storeControllerSubmitContact(data);
    unwrapData<unknown>(res);
  },

  getNewsList: async (params?: {
    page?: number;
    pageSize?: number;
  }): Promise<{ list: NewsListItem[]; total: number }> => {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 10;
    const res = await CService.cmsControllerNews(page, pageSize);
    const d = unwrapData<{ list?: NewsListItem[]; total?: number }>(res);
    return {
      list: d?.list ?? [],
      total: d?.total ?? 0,
    };
  },

  getNewsDetail: async (id: string): Promise<NewsDetailData> => {
    const numId = parseInt(id, 10);
    if (Number.isNaN(numId)) throw new Error('Invalid news id');
    const res = await CService.cmsControllerNewsDetail(numId);
    return unwrapData<NewsDetailData>(res);
  },

  getFaq: async (): Promise<FaqItem[]> => {
    const res = await CService.cmsControllerFaq();
    const d = unwrapData<{ list?: FaqItem[] }>(res);
    return d?.list ?? [];
  },

  /** 按 sessionId 拉取该会话消息，sessionId 必传 */
  getChatMessages: async (sessionId: string): Promise<ChatMessage[]> => {
    const res = await CStoreService.storeControllerGetChatMessages(sessionId);
    const d = unwrapData<{ list?: ChatMessage[] }>(res);
    return d?.list ?? [];
  },

  /** 发送客户消息，sessionId 必填，userId 可选 */
  sendChatMessage: async (
    content: string,
    sessionId: string,
    userId?: string
  ): Promise<{ message: ChatMessage }> => {
    const res = await CStoreService.storeControllerSendChat({
      content,
      sessionId,
      userId,
    });
    const d = unwrapData<{ message?: ChatMessage }>(res);
    if (d?.message) return { message: d.message };
    const fallback: ChatMessage = {
      id: `client-${Date.now()}`,
      content,
      from: 'client',
      createdAt: new Date().toISOString(),
    };
    return { message: fallback };
  },
};
